import React, { useCallback, useEffect } from 'react';

import { useIntl } from 'react-intl-next';

import { aiProactiveMessages } from '@atlaskit/editor-common/ai-messages';
import {
	ACTION,
	ACTION_SUBJECT,
	ACTION_SUBJECT_ID,
	EVENT_TYPE,
} from '@atlaskit/editor-common/analytics';
import { useSharedPluginState } from '@atlaskit/editor-common/hooks';
import { useSharedPluginStateSelector } from '@atlaskit/editor-common/use-shared-plugin-state-selector';
import { fg } from '@atlaskit/platform-feature-flags';
import { createUnifiedAnalyticsPayload } from '@atlassian/editor-ai-common/analytics/create-unified-analytics-payload';
import { copyPMFragmentToClipboard } from '@atlassian/editor-ai-common/utils/clipboard';
import { type PreviewButton, PreviewScreen } from '@atlassian/generative-ai-modal/screens/Preview';

import {
	clearSelectRecommendation,
	insertRecommendation,
	removeRecommendation,
} from '../../../pm-plugins/ai-proactive/commands';
import { getRecommendationCard } from '../../../pm-plugins/ai-proactive/suggestion-setting-map';
import { getBlockFromRecommendationId } from '../../../pm-plugins/ai-proactive/utils';
import type { FeedbackMetadata, ProactiveFeedbackMetadata } from '../../../types';
import { useContentComponentContext } from '../../hooks/useContentComponentContext';
import { useProactivePluginState } from '../../hooks/useProactivePluginState';
import { launchProactiveFeedbackDialog } from '../../utils/feedback';
import { PreviewRenderer } from '../PreviewRenderer/PreviewRenderer';

export function PreviewScreenWithLogic() {
	const {
		editorView,
		api,
		providerFactory,
		config: { handleFeedbackSubmission, product },
	} = useContentComponentContext();

	const analyticsApi = api?.analytics?.actions;
	const { editorDisabledState } = useSharedPluginState(api, ['editorDisabled']);
	const { formatMessage, locale } = useIntl();
	const [feedbackSubmitted, setFeedbackSubmitted] = React.useState(false);

	const selectedRecommendationId = useSharedPluginStateSelector(
		api,
		'aiProactive.selectedRecommendationId',
	);
	const analyticsAIInteractionId = useSharedPluginStateSelector(
		api,
		'aiProactive.analyticsAIInteractionId',
	);

	const aiProactivePluginState = useProactivePluginState(editorView);

	const { block, recommendation } = selectedRecommendationId
		? getBlockFromRecommendationId(aiProactivePluginState, selectedRecommendationId)
		: { block: undefined, recommendation: undefined };

	useEffect(() => {
		if (recommendation?.transformAction) {
			// don't use the local analyticsApi, it changes many times, and will trigger the useEffect/event many times
			api?.analytics?.actions?.fireAnalyticsEvent(
				createUnifiedAnalyticsPayload({
					action: ACTION.VIEWED,
					interactionID: analyticsAIInteractionId,
					experienceName: recommendation.transformAction,
					isProactive: true,
					attributes: {},
				}),
			);
		}
	}, [api, analyticsAIInteractionId, recommendation?.transformAction]);

	const onClose = useCallback(() => {
		clearSelectRecommendation()(editorView.state, editorView.dispatch);
	}, [editorView.dispatch, editorView.state]);

	if (!selectedRecommendationId || !block || !recommendation) {
		return null;
	}

	const { title } = getRecommendationCard(recommendation);

	const preview = (
		<PreviewRenderer
			jsonDocument={recommendation.transformContent.document}
			showTelepointer={false}
			providerFactory={providerFactory}
			reduceMargins
		/>
	);

	const proactiveRecommendationButtons: PreviewButton[] = [
		{
			label: formatMessage(aiProactiveMessages.dismissProactiveRecommendation),
			appearance: 'cancel',
			onClick: () => {
				if (!recommendation) {
					return null;
				}
				removeRecommendation({
					analyticsApi,
					recommendationId: recommendation.id,
					triggeredFrom: 'preview',
					feedback: {
						feedbackSubmitted,
						product,
						locale,
						editorView,
						handleFeedbackSubmission,
					},
				})(editorView.state, editorView.dispatch);
			},
			isDisabled: editorDisabledState?.editorDisabled,
		},
		{
			label: formatMessage(aiProactiveMessages.insertBelowProactiveRecommendation),
			appearance: 'secondary',
			onClick: () => {
				if (!recommendation) {
					return null;
				}
				insertRecommendation({
					analyticsApi,
					recommendationId: recommendation.id,
					triggeredFrom: 'preview',
					insertionMethod: 'insertBelow',
				})(editorView.state, editorView.dispatch);
			},
			isDisabled: editorDisabledState?.editorDisabled,
		},
		{
			label: formatMessage(aiProactiveMessages.replaceProactiveRecommendation),
			appearance: 'primary',
			onClick: () => {
				if (!recommendation) {
					return null;
				}
				insertRecommendation({
					analyticsApi,
					recommendationId: recommendation.id,
					triggeredFrom: 'preview',
					insertionMethod: 'replace',
				})(editorView.state, editorView.dispatch);
			},
			isDisabled: editorDisabledState?.editorDisabled,
		},
	];

	const fireResponseFeedbackEvent = (sentiment: FeedbackMetadata['sentiment']) => {
		analyticsApi?.fireAnalyticsEvent({
			eventType: EVENT_TYPE.UI,
			action: ACTION.CLICK,
			actionSubject: ACTION_SUBJECT.EDITOR_PLUGIN_AI,
			actionSubjectId: ACTION_SUBJECT_ID.RESPONSE_FEEDBACK_BUTTON,
			attributes: {
				sentiment,
				product,
				configItemTitle: 'proactive-recommendation',
			},
		});
	};

	const handleFeedbackButtonClick = async (sentiment: ProactiveFeedbackMetadata['sentiment']) => {
		fireResponseFeedbackEvent(sentiment);
		setFeedbackSubmitted(true);

		if (fg('platform_editor_ai_aggressive_feedback_proactive')) {
			launchProactiveFeedbackDialog({
				recommendationId: recommendation.id,
				aiProactivePluginState,
				editorView,
				locale,
				sentiment,
				product,
				handleFeedbackSubmission,
				triggeredFrom: 'preview',
				fireAnalyticsEvent: analyticsApi?.fireAnalyticsEvent,
			});
		} else {
			if (!handleFeedbackSubmission) {
				return false;
			}
			await handleFeedbackSubmission({
				sentiment,
				getAIExperience: (hasUserConsent) => ({
					browserLocale: window.navigator.languages,
					userLocale: locale,
					...(hasUserConsent
						? {
								originalParagraph: block.text,
								suggestedParagraph: recommendation.transformContent.document.content,
								recommendationId: recommendation.id,
							}
						: {}),
				}),
				editorAttributes: { product },
			});
		}
	};

	return (
		<PreviewScreen
			staticPromptTitle={formatMessage(title)}
			hideHorizontalRule
			showBetaLozenge
			preview={preview}
			disableActions={false}
			disableInterrogation
			schema={editorView.state.schema}
			providerFactory={providerFactory}
			onCopy={() => {
				analyticsApi?.fireAnalyticsEvent({
					eventType: EVENT_TYPE.UI,
					action: ACTION.CLICK,
					actionSubject: ACTION_SUBJECT.EDITOR_PLUGIN_AI,
					actionSubjectId: ACTION_SUBJECT_ID.COPY_BUTTON,
					attributes: {
						experienceType: 'proactive-recommendation',
					},
				});

				const pmFragment = recommendation.transformContent.getContent();
				copyPMFragmentToClipboard({ editorView, pmFragment });
			}}
			showBack={false}
			onGoodResponseButtonClick={() => handleFeedbackButtonClick('good-recommendation')}
			onBadResponseButtonClick={() => handleFeedbackButtonClick('bad-recommendation')}
			onClose={onClose}
			applications={proactiveRecommendationButtons}
			isInputActive={false}
			isClearTitle
		/>
	);
}
